import React, { RefObject, useCallback, useState } from "react";
import { FeedFollowCtaWithDetails } from "web/react/components/feed-follow-cta-with-details";
import { FeedHeaderTitle } from "web/react/components/feed-header-title/feed-header-title";
import { PromotionHeader } from "web/react/components/promotion-header";
import { PromotionInfo } from "web/react/components/promotion-summary/promotion-info/promotion-info";
import { StorefrontDescription } from "web/react/components/storefront-description";
import { StorefrontModal } from "web/react/components/storefront-modal";
import { StorefrontInfo } from "web/react/components/storefront-modal/storefront-info";
import { CircleLogo } from "web/react/emo/circle-logo";
import { HStack } from "web/react/emo/h-stack";
import { Sticker } from "web/react/emo/sticker";
import { TertiaryArticleCard } from "web/react/emo/tertiary-article-card";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { FeedPage } from "web/react/pages/feed/utils";
import { gettext } from "web/script/modules/django-i18n";
import { canUseMembership } from "web/script/modules/utils";
import {
    ArticleCardSerializer,
    FeedHeaderSerializer,
    RetailerPromotionsSerializer,
} from "web/types/serializers";
import * as styles from "./storefront-feed-header.css";

export interface StorefrontFeedHeaderProps {
    promotions?: RetailerPromotionsSerializer | null;
    title: string;
    profile: FeedHeaderSerializer;
    shortDescription?: string | null;
    description?: string | null;
    followCTARef?: RefObject<HTMLDivElement>;
    articles: ArticleCardSerializer[];
}

export function StorefrontFeedHeader({
    promotions,
    profile,
    title,
    shortDescription,
    description,
    followCTARef,
    articles,
}: StorefrontFeedHeaderProps): JSX.Element {
    const { isMobileViewport } = useDomViewport();
    const [isInfoModalOpen, setInfoModalOpen] = useState(false);
    const [isPromoModalOpen, setPromoModalOpen] = useState(false);

    const isCheckoutVerified = !!profile.checkout_verified;

    const isOfficialStore = false; // TODO BEAM-998 https://lyst.atlassian.net/browse/BEAM-998
    const headerImageSrc = (profile.photo_url || profile.small_photo_url) ?? undefined;
    const headerImageAltText = profile.photo_alt_text ?? undefined;
    const headerImageIsPlaceholder = headerImageSrc?.includes("default-user.jpg") || false;
    const designerName = profile.follow_button?.designer_name ?? undefined;
    const promotionsTitle = profile.designer_name ?? title;
    const descriptionToUse = description || shortDescription;

    const onDescriptionClick = useCallback(() => {
        setInfoModalOpen(true);
    }, []);

    const closeInfoModal = useCallback(() => {
        setInfoModalOpen(false);
    }, []);

    const onPromoClick = useCallback(() => {
        setPromoModalOpen(true);
    }, [title]);

    const closePromoModal = useCallback(() => {
        setPromoModalOpen(false);
    }, []);

    return (
        <>
            <div className={styles.wrapper} ref={followCTARef}>
                <div className={styles.headerMainContent}>
                    <View marginBottom={{ sm: "sm", md: "md" }} className={styles.title}>
                        <HStack spacing={isMobileViewport ? "sm" : "md"}>
                            <CircleLogo
                                size="lg"
                                imageURL={headerImageIsPlaceholder ? "" : headerImageSrc}
                                name={designerName || ""}
                                altText={headerImageAltText}
                            />
                            <VStack spacing="xxs">
                                <FeedHeaderTitle
                                    title={title}
                                    type={FeedPage.RETAILER_PROFILE}
                                    showVerifiedTick={isCheckoutVerified}
                                />
                                {canUseMembership() && (
                                    <FeedFollowCtaWithDetails
                                        followButton={profile.follow_button}
                                        followersCountLabel={profile.followers_count_label}
                                        designerName={title}
                                    />
                                )}
                            </VStack>
                        </HStack>
                    </View>
                    {(isCheckoutVerified || isOfficialStore) && (
                        <View marginBottom="sm">
                            <HStack spacing="xxs">
                                {isCheckoutVerified && <Sticker name="lystShopPartner" />}
                                {isOfficialStore && <Sticker name="officialStore" />}
                            </HStack>
                        </View>
                    )}
                    <div>
                        <StorefrontDescription
                            description={descriptionToUse}
                            beforeOnClick={onDescriptionClick}
                            retailer={title}
                            displayAsPreview={true}
                            parentArea="header"
                            retailerNetwork={profile.retailer_network}
                        />
                    </div>
                </div>
                {!!promotions && (
                    <div className={styles.promotionContainer}>
                        <PromotionHeader
                            promotions={promotions}
                            title={promotionsTitle}
                            parentArea="header"
                            onClick={onPromoClick}
                            retailerNetwork={profile.retailer_network}
                        />
                        <StorefrontModal
                            title={gettext("promotion.button.see_all")}
                            isOpen={isPromoModalOpen}
                            onClose={closePromoModal}
                        >
                            <PromotionInfo promotions={promotions} />
                        </StorefrontModal>
                    </div>
                )}
            </div>
            {articles.length ? (
                <View dataTestId="storefront-articles" marginBottom={{ sm: "md", lg: "xxl" }}>
                    <View marginBottom="sm">
                        <Text textStyle="body-bold">{gettext("storefront.articles.title")}</Text>
                    </View>
                    <View paddingBottom="sm" className={styles.articlesContainer}>
                        {articles.map((article, i) => (
                            <TertiaryArticleCard
                                key={`${article.slug}-${i}`}
                                // TODO BEAM-1111 https://lyst.atlassian.net/browse/BEAM-1111
                                // send onClick analytics event
                                onClick={() => {}}
                                {...article}
                            />
                        ))}
                    </View>
                </View>
            ) : null}
            <StorefrontModal
                isOpen={isInfoModalOpen}
                onClose={closeInfoModal}
                title={title}
                showCheckoutVerifiedBadge={isCheckoutVerified}
            >
                <StorefrontInfo
                    dateAdded={profile.date_added || ""}
                    description={description || shortDescription || null}
                    isCheckout={isCheckoutVerified}
                    isOfficialStore={isOfficialStore}
                    bestShippingOption={profile.best_shipping_option || ""}
                    shipsToCountry={profile.ships_to_country || ""}
                    parentArea="header"
                    retailerNetwork={profile.retailer_network}
                />
            </StorefrontModal>
        </>
    );
}
